import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { RequiredUtilsModule } from '@lib/util';
import { FrameContractSelection } from '@lib/frame-contract-selection';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'basket-supplier-frame-contract-selection',
    imports: [CommonModule, TranslateModule, RequiredUtilsModule, ReactiveFormsModule],
    templateUrl: './basket-supplier-frame-contract-selection.component.html',
    styleUrl: './basket-supplier-frame-contract-selection.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BasketSupplierFrameContractSelectionComponent implements OnInit, OnChanges, OnDestroy {
    @Input() public frameContractSelection?: FrameContractSelection | null;
    @Input() public editable = false;
    @Input() public frameContracts!: FrameContractSelection[] | null;
    @Input() public generalAgreementRequired = false;

    @Output() public frameContractSelectionChanged = new EventEmitter<number | null>();
    @Output() public frameContractValidated = new EventEmitter<boolean>();

    public readonly control = new FormControl<number | null>(null);

    private readonly destroy$ = new Subject<void>();

    public ngOnInit(): void {
        this.control.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((value) => {
            const previousId = this.frameContractSelection?.id ?? null;
            if (value === previousId) {
                return;
            }
            this.frameContractSelectionChanged.emit(value ?? null);
        });
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['frameContractSelection'] || changes['frameContracts']) {
            const selectedId = this.frameContractSelection?.id ?? null;
            if (this.control.value !== selectedId) {
                this.control.setValue(selectedId, { emitEvent: false });
            }
        }
        if (changes['editable']) {
            if (this.editable) {
                this.control.enable({ emitEvent: false });
            } else {
                this.control.disable({ emitEvent: false });
            }
        }
        if (changes['generalAgreementRequired'] || changes['frameContracts'] || changes['frameContractSelection']) {
            this.frameContractValidated.emit(this.isValid());
        }
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public get isVisible(): boolean {
        const hasContracts = !!this.frameContracts && this.frameContracts.length > 0;
        const hasSelection = !!this.frameContractSelection;
        return hasContracts || hasSelection || this.generalAgreementRequired;
    }

    public get hasContracts(): boolean {
        return !!this.frameContracts && this.frameContracts.length > 0;
    }

    public get showRequiredButEmptyWarning(): boolean {
        return this.generalAgreementRequired && !this.hasContracts;
    }

    public get showRequiredButUnselectedError(): boolean {
        return this.generalAgreementRequired && this.hasContracts && this.frameContractSelection == null;
    }

    private isValid(): boolean {
        if (!this.generalAgreementRequired) {
            return true;
        }
        return this.frameContractSelection != null;
    }
}
